// Single source of truth for test-drive booking time slots. Used by the
// frontend modal (to render the dropdown) and the server route (to validate
// incoming bookings). Keep these tables in sync with the dealership's
// published opening hours.

export const WEEKDAY_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00',
  '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
];

export const SATURDAY_SLOTS = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00',
  '11:30', '12:00',
];

// First slot in each list is rendered up-front; the remainder is gated behind
// a "show more times" toggle in the modal.
export const WEEKDAY_INITIAL_COUNT = 9;
export const SATURDAY_INITIAL_COUNT = 7;

export type CalendarDateLike = { year: number; month: number; day: number };

// Returns the local-calendar weekday (Sun=0..Sat=6) for a CalendarDate-like
// object. Avoid `new Date(calendarDate)` / `new Date("YYYY-MM-DD")`: those
// parse as UTC midnight, so `.getDay()` returns the wrong weekday in any
// non-UTC browser timezone (e.g. negative offsets shift Saturday to Friday).
export const getCalendarDayOfWeek = (date?: CalendarDateLike): number => {
  if (!date) return -1;
  return new Date(date.year, date.month - 1, date.day).getDay();
};

export const isValidBookingSlot = (date: CalendarDateLike, time: string): boolean => {
  const dayOfWeek = getCalendarDayOfWeek(date);
  if (dayOfWeek === 0) return false; // Sunday closed
  if (dayOfWeek === 6) return SATURDAY_SLOTS.includes(time);
  return WEEKDAY_SLOTS.includes(time);
};

// Workshop booking is closed on weekends. Mo-Thu close at 15:00, Fri at 13:30.
export const WORKSHOP_MO_THU_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00',
  '13:30', '14:00', '14:30', '15:00',
];

export const WORKSHOP_FRI_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
];

export const WORKSHOP_WEEKDAY_INITIAL_COUNT = 9;

export const getWorkshopSlotsForDay = (dayOfWeek: number): readonly string[] | null => {
  if (dayOfWeek === 0 || dayOfWeek === 6) return null; // closed Sat & Sun
  if (dayOfWeek === 5) return WORKSHOP_FRI_SLOTS;
  return WORKSHOP_MO_THU_SLOTS;
};

export const isValidWorkshopBookingSlot = (date: CalendarDateLike, time: string): boolean => {
  const slots = getWorkshopSlotsForDay(getCalendarDayOfWeek(date));
  return slots !== null && slots.includes(time);
};

// Minimum lead time before a workshop booking: the earliest selectable date is
// today + WORKSHOP_MIN_LEAD_DAYS (e.g. booking on the 26th → earliest is the 9th
// of the following month for a 14-day lead). Enforced in the calendar UI and
// re-validated on the server so a crafted request cannot book sooner.
export const WORKSHOP_MIN_LEAD_DAYS = 28;

export const isWorkshopDateTooEarly = (date: CalendarDateLike): boolean => {
  const earliest = new Date();
  earliest.setHours(0, 0, 0, 0);
  earliest.setDate(earliest.getDate() + WORKSHOP_MIN_LEAD_DAYS);

  const dateObj = new Date(date.year, date.month - 1, date.day);
  return dateObj < earliest;
};
